import logo from 'assets/header-prevencor-xx.png';
import md5 from 'md5';
import pdfMake from 'pdfmake/build/pdfmake';
import pdfFonts from 'pdfmake/build/vfs_fonts';
import fetchImageBase64 from 'utils/helpers';
import {
  getStressRichTextContent,
  hasStressDocumentContent,
  regexTopicos,
  splitMeasureColumns,
} from './stressRichText';

pdfMake.vfs = pdfFonts.pdfMake.vfs;

function buildPdfRun(text, style = {}) {
  const run = { text };
  if (style.bold) run.bold = true;
  if (style.italics) run.italics = true;
  if (style.underline) run.decoration = 'underline';
  return run;
}

function collectInlineRuns(node, style = { bold: false, italics: false, underline: false }) {
  if (!node) return [];

  if (node.nodeType === Node.TEXT_NODE) {
    const rawText = node.textContent || '';
    if (!rawText) return [];
    return [buildPdfRun(rawText, style)];
  }

  if (node.nodeType !== Node.ELEMENT_NODE) {
    return [];
  }

  const tagName = node.tagName.toUpperCase();
  if (tagName === 'BR') {
    return [{ text: '\n' }];
  }

  const nextStyle = {
    bold:
      style.bold || tagName === 'B' || tagName === 'STRONG' || tagName === 'H4' || tagName === 'H5',
    italics: style.italics || tagName === 'I' || tagName === 'EM',
    underline: style.underline || tagName === 'U',
  };

  const runs = [];
  Array.from(node.childNodes).forEach((child) => {
    runs.push(...collectInlineRuns(child, nextStyle));
  });

  return runs;
}

function applyHeadingFallbackIfUnstyled(runs) {
  if (!runs || runs.length === 0) return runs;

  const hasExplicitStyle = runs.some((r) => r.bold || r.italics || r.decoration);
  if (hasExplicitStyle) return runs;

  const fullText = runs.map((r) => r.text || '').join('');
  const trimmed = fullText.trim();
  if (!trimmed) return runs;

  const matchTopico = trimmed.match(regexTopicos);
  if (matchTopico) {
    const topico = matchTopico[0];
    const idx = fullText.indexOf(topico);
    if (idx !== -1) {
      const before = fullText.slice(0, idx);
      const after = fullText.slice(idx + topico.length);
      const result = [];
      if (before) result.push({ text: before });
      result.push({ text: topico, bold: true });
      if (after) result.push({ text: after });
      return result;
    }
  }

  if (trimmed.endsWith(':') && trimmed.length < 65 && !trimmed.includes('..')) {
    return [{ text: trimmed, bold: true }];
  }

  return runs;
}

function convertParagraphElementToPdfNode(element) {
  const rawRuns = collectInlineRuns(element);
  const fullText = rawRuns.map((r) => r.text || '').join('');

  if (!fullText.replace(/\u00A0/g, ' ').trim()) {
    return { text: ' ', margin: [0, 2, 0, 2] };
  }

  // Se a linha for um par de medidas lado a lado (vindo do legado RTF ou digitado manualmente),
  // renderiza em 2 colunas proporcionais no pdfMake para não desalinhar com a fonte Roboto.
  const measureColumns = splitMeasureColumns(fullText);
  if (measureColumns) {
    const [leftCol, rightCol] = measureColumns;
    return {
      columns: [
        { text: leftCol, width: '52%' },
        { text: rightCol || '', width: '48%' },
      ],
      columnGap: 8,
      margin: [0, 0, 0, 1],
    };
  }

  const styledRuns = applyHeadingFallbackIfUnstyled(rawRuns);
  const isHeadingBlock =
    styledRuns.length > 0 &&
    styledRuns[0]?.bold &&
    (regexTopicos.test(fullText.trim()) ||
      (fullText.trim().endsWith(':') && fullText.trim().length < 65));

  return {
    text: styledRuns,
    preserveLeadingSpaces: true,
    margin: isHeadingBlock ? [0, 4, 0, 2] : [0, 0, 0, 1],
  };
}

function convertTableElementToPdfNode(tableElement) {
  const rows = Array.from(tableElement.querySelectorAll('tr'));
  if (rows.length === 0) return null;

  let maxCols = 0;
  const body = rows.map((tr) => {
    const cells = Array.from(tr.querySelectorAll('th, td'));
    if (cells.length > maxCols) maxCols = cells.length;
    return cells.map((cell) => {
      const cellRuns = collectInlineRuns(cell);
      const cellText = cellRuns
        .map((r) => r.text || '')
        .join('')
        .trim();
      return {
        text: cellText ? cellRuns : ' ',
        border: [false, false, false, false],
        margin: [0, 0, 6, 1],
      };
    });
  });

  if (maxCols === 0) return null;

  // Garante que todas as linhas tenham o mesmo número de células para o pdfMake
  const normalizedBody = body.map((row) => {
    const padded = [...row];
    while (padded.length < maxCols) {
      padded.push({ text: ' ', border: [false, false, false, false], margin: [0, 0, 0, 1] });
    }
    return padded;
  });

  const widths = maxCols === 2 ? ['52%', '48%'] : Array(maxCols).fill('*');

  return {
    table: {
      widths,
      body: normalizedBody,
    },
    layout: 'noBorders',
    margin: [0, 1, 0, 2],
  };
}

function convertListElementToPdfNode(listElement, ordered = false) {
  const items = Array.from(listElement.children)
    .filter((el) => el.tagName?.toUpperCase() === 'LI')
    .map((li) => {
      const runs = collectInlineRuns(li);
      return { text: runs.length > 0 ? runs : ' ', margin: [0, 0, 0, 1] };
    });

  if (items.length === 0) return null;

  return ordered ? { ol: items, margin: [12, 2, 0, 4] } : { ul: items, margin: [12, 2, 0, 4] };
}

/**
 * Converte o HTML rico sanitizado do laudo de Stress diretamente em nós do pdfMake,
 * preservando negrito, itálico, sublinhado, listas e tabelas/colunas de medidas.
 */
export function htmlToPdfMakeContent(html) {
  if (!hasStressDocumentContent(html)) {
    return [{ text: 'Sem descrição preenchida.', margin: [10, 0, 0, 1] }];
  }

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');
  const blocks = [];

  const processNode = (node) => {
    if (!node) return;

    if (node.nodeType === Node.TEXT_NODE) {
      const text = node.textContent || '';
      if (!text.trim()) return;
      text.split('\n').forEach((line) => {
        const spanWrapper = doc.createElement('p');
        spanWrapper.textContent = line;
        blocks.push(convertParagraphElementToPdfNode(spanWrapper));
      });
      return;
    }

    if (node.nodeType !== Node.ELEMENT_NODE) return;

    const tagName = node.tagName.toUpperCase();

    if (tagName === 'BR') {
      blocks.push({ text: ' ', margin: [0, 2, 0, 2] });
      return;
    }

    if (tagName === 'TABLE') {
      const tableNode = convertTableElementToPdfNode(node);
      if (tableNode) blocks.push(tableNode);
      return;
    }

    if (tagName === 'UL' || tagName === 'OL') {
      const listNode = convertListElementToPdfNode(node, tagName === 'OL');
      if (listNode) blocks.push(listNode);
      return;
    }

    if (['P', 'DIV', 'H4', 'H5'].includes(tagName)) {
      // Se um DIV/P contiver blocos filhos (ex: P ou TABLE aninhados pelo contentEditable), processa recursivamente
      const hasBlockChildren = Array.from(node.children || []).some((child) =>
        ['P', 'DIV', 'TABLE', 'UL', 'OL', 'H4', 'H5'].includes(child.tagName?.toUpperCase())
      );
      if (hasBlockChildren) {
        Array.from(node.childNodes).forEach(processNode);
        return;
      }

      blocks.push(convertParagraphElementToPdfNode(node));
      return;
    }

    // Elementos inline soltos na raiz (ex: STRONG, EM, U)
    const wrapper = doc.createElement('p');
    wrapper.appendChild(node.cloneNode(true));
    blocks.push(convertParagraphElementToPdfNode(wrapper));
  };

  Array.from(doc.body.childNodes).forEach(processNode);

  return blocks.length > 0
    ? blocks
    : [{ text: 'Sem descrição preenchida.', margin: [10, 0, 0, 1] }];
}

export async function generatePDFStress({ stress }) {
  const safeStress = stress || {};
  const htmlLaudo = getStressRichTextContent(safeStress);
  const logoBase64 = await fetchImageBase64(logo);
  const hashAssinatura = safeStress.HASH || md5(JSON.stringify(safeStress));

  const laudoFormatadoPdfMake = htmlToPdfMakeContent(htmlLaudo);

  const content = [
    {
      text: 'Laudo de Ecocardiograma de Stress',
      bold: true,
      decoration: 'underline',
      alignment: 'center',
      fontSize: 14,
      margin: [0, 0, 0, 2],
    },
    {
      text: String(safeStress.TIPOSTRESS || ''),
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 10],
    },
    {
      text: [
        { text: 'Paciente: ', bold: true },
        `${safeStress.CODPAC || '-'} ${safeStress.NOMEPAC || '-'}`,
      ],
      margin: [0, 0, 0, 1],
    },
    {
      columns: [
        {
          text: [
            { text: 'Sexo: ', bold: true },
            String(safeStress.SEXOPAC || '-'),
            { text: '   Idade: ', bold: true },
            `${safeStress.IDADEPAC ?? '-'} anos`,
          ],
          width: '*',
        },
        {
          text: [{ text: 'Data do Exame: ', bold: true }, String(safeStress.dataFormatada || '-')],
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 1, 0, 1],
    },
    {
      columns: [
        {
          text: [
            { text: 'Médico que Realizou: ', bold: true },
            `Dr.(a): ${safeStress.Realizou || '-'}`,
          ],
          width: '*',
        },
        {
          text: String(safeStress.CODSTRESS || ''),
          bold: true,
          width: 'auto',
          alignment: 'right',
        },
      ],
      margin: [0, 1, 0, 1],
    },
    {
      text: [
        { text: 'Médico que Solicitou: ', bold: true },
        `Dr.(a): ${safeStress.Solicitou || '-'}`,
      ],
      margin: [0, 1, 0, 1],
    },
    {
      text: [{ text: 'Convênio: ', bold: true }, String(safeStress.SIGLACONV || '-')],
      margin: [0, 1, 0, 6],
    },
    {
      canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
      margin: [0, 0, 0, 6],
    },

    {
      stack: laudoFormatadoPdfMake,
      margin: [10, 0, 0, 10],
    },

    {
      text: 'Conferido e assinado eletronicamente por:',
      italics: true,
      alignment: 'center',
      margin: [0, 20, 0, 3],
    },
    {
      text: `Dr(a).: ${safeStress.Realizou || '-'}`,
      bold: true,
      alignment: 'center',
      margin: [0, 0, 0, 3],
    },
    {
      text:
        [
          safeStress.CPFMED ? `CPF: ${safeStress.CPFMED}` : null,
          safeStress.CRMMED ? `CRM: ${safeStress.CRMMED}` : null,
          safeStress.RQEMED ? `RQE: ${safeStress.RQEMED}` : null,
        ]
          .filter(Boolean)
          .join(' - ') || '-',
      bold: true,
      alignment: 'center',
      fontSize: 9,
      margin: [0, 0, 0, 3],
    },
    {
      text: `Assinatura Digital: ${hashAssinatura}`,
      bold: true,
      italics: true,
      alignment: 'center',
      fontSize: 9,
      margin: [0, 0, 0, 12],
    },
  ];

  const docDefinition = {
    pageSize: 'A4',
    pageMargins: [40, 90, 40, 40],
    header: () => ({
      stack: [
        {
          image: logoBase64,
          width: 460,
          alignment: 'center',
          margin: [0, 6, 0, 2],
        },
        {
          text: String(safeStress.CODSTRESS || ''),
          bold: true,
          alignment: 'right',
          fontSize: 10,
          margin: [40, 0, 40, 0],
        },
      ],
    }),
    footer: (currentPage, pageCount) => [
      {
        canvas: [{ type: 'line', x1: 0, y1: 0, x2: 515, y2: 0, lineWidth: 0.5 }],
        margin: [40, 2, 40, 0],
      },
      {
        text: `Pag: ${currentPage} de ${pageCount}`,
        alignment: 'center',
        fontSize: 8,
        bold: true,
        margin: [40, 5, 40, 0],
      },
    ],
    content,
    defaultStyle: { fontSize: 12 },
  };

  pdfMake.createPdf(docDefinition).open();
  return docDefinition;
}

export default async function generateStressPdf(stress) {
  return generatePDFStress({ stress });
}
